import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { SiteGroupV2, SiteV1, SiteV2 } from 'src/app/shared/models';
import { API_URL_COMPANIES_V2 } from 'src/app/shared/services/basic.service';
import { accountTypes } from 'src/app/shared/strategies/account-types.const';
import { StrategiesService } from 'src/app/shared/strategies/strategies.service';

@Injectable({
  providedIn: 'root',
})
export class SitesService {
  sitesV2: SiteV2[] = [];
  sitesV2Map: Map<number, SiteV2> = new Map();
  siteGroupsV2: SiteGroupV2[];
  siteGroupsV2Map: Map<number, SiteGroupV2> = new Map();

  private sitesResolver$;
  private sitesMapResolver$;
  private siteGroupsResolver$;
  private siteGroupsMapResolver$;

  private sitesIdsV2Set = new Set();

  constructor(
    private httpClient: HttpClient,
    private alcmeonContextService: AlcmeonContextService,
    private strategiesService: StrategiesService
  ) {}

  getSitesV2(): Observable<SiteV2[]> {
    return this.httpClient.get<SiteV2[]>(`${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/sites`).pipe(
      map(sites => {
        sites.sort(this.sortByTypeThenName);
        const sitesFormatted = sites.map((site: SiteV2) => {
          const siteFormatted = this.formatSiteV2({
            site,
            id: site.id,
            siteGroupId: null,
            type: site.type,
          });
          this.sitesIdsV2Set.add(site.id);
          this.sitesV2Map.set(site.id, siteFormatted);
          return siteFormatted;
        });
        this.sitesV2 = Array.from(this.sitesIdsV2Set).map(id => this.sitesV2Map.get(+id));
        this.sitesV2.sort(this.sortByTypeThenName);
        return sitesFormatted;
      })
    );
  }

  getSiteGroupsV2(): Observable<SiteGroupV2[]> {
    return this.httpClient
      .get<SiteGroupV2[]>(`${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/site-groups`)
      .pipe(
        tap(siteGroups => {
          this.sitesIdsV2Set = new Set();
          this.siteGroupsV2 = [].concat(siteGroups);
          this.siteGroupsV2.forEach(siteGroup => {
            (Object.keys(siteGroup.sites) || []).forEach((siteId: string) => {
              const site = siteGroup.sites[+siteId];
              const siteFormatted = this.formatSiteV2({
                site,
                id: +siteId,
                siteGroupId: siteGroup.id,
                type: siteGroup.type,
              });
              this.sitesV2Map.set(+siteId, siteFormatted);
              this.sitesIdsV2Set.add(siteId);
              siteGroup.sites[+siteId] = siteFormatted;
            });
            siteGroup.type_name = accountTypes[siteGroup.type];
            const strategy = this.strategiesService.strategiesMap.get(siteGroup.type);

            this.siteGroupsV2Map.set(siteGroup.id, {
              ...siteGroup,
              isGroup: true,
              quick_replies_enabled: strategy?.isQuickReplyEnable() ?? 0,
              typeName: accountTypes[siteGroup.type],
            });
          });
        })
      );
  }

  formatSiteV2({ site, id, siteGroupId, type }): SiteV2 {
    const strategy = this.strategiesService.strategiesMap.get(type);
    return {
      ...site,
      id: +id,
      quick_replies_enabled: strategy?.isQuickReplyEnable() ?? 0,
      site_group_id: siteGroupId,
      type,
      type_name: accountTypes[type],
      typeName: accountTypes[type],
      label: site.name,
      siteName: site.name, // to remove once legacy icons usages are removed.
    };
  }

  getSitesResolver(): Promise<any> {
    if (!this.sitesResolver$) {
      this.sitesResolver$ = this.wrapResolver(() => {
        let sitesGrouped = [];
        for (let index = 0; index < this.siteGroupsV2.length; index++) {
          const siteGroup = this.siteGroupsV2[index];
          const sites = (Object.keys(siteGroup.sites) || []).map(siteId => {
            return siteGroup.sites[+siteId];
          });

          sitesGrouped = sitesGrouped.concat(sites || []);
        }
        return this.getSitesV2()
          .pipe(switchMap(result => of(sitesGrouped.concat(result))))
          .toPromise();
      });
    }
    return this.sitesResolver$;
  }

  getSitesMapResolver(): Promise<any> {
    if (!this.sitesMapResolver$) {
      this.sitesMapResolver$ = this.wrapResolver(() => new Promise(resolve => resolve(new Map(this.sitesV2Map))));
    }
    return this.sitesMapResolver$;
  }

  getSiteGroupsResolver(): Promise<any> {
    if (!this.siteGroupsResolver$) {
      this.siteGroupsResolver$ = this.wrapResolver(() => this.getSiteGroupsV2().toPromise());
    }
    return this.siteGroupsResolver$;
  }

  getSiteGroupsMapResolver(): Promise<any> {
    if (!this.siteGroupsMapResolver$) {
      this.siteGroupsMapResolver$ = this.wrapResolver(
        () => new Promise(resolve => resolve(new Map(this.siteGroupsV2Map)))
      );
    }
    return this.siteGroupsMapResolver$;
  }

  sortByTypeThenName = (a: SiteV1 | SiteV2, b: SiteV1 | SiteV2) => {
    const result = a.type - b.type;
    if (!result) {
      const aLabel = a.name || a['label'];
      const bLabel = b.name || b['label'];
      return aLabel.localeCompare(bLabel);
    }
    return result;
  };

  refreshResolvers() {
    return new Promise(resolve => {
      this.siteGroupsResolver$.then(siteGroupsResolver => {
        siteGroupsResolver.refresh().then(() => {
          this.siteGroupsMapResolver$.then(siteGroupsMapResolver => {
            siteGroupsMapResolver.refresh();
            this.sitesResolver$.then(sitesResolver => {
              sitesResolver.refresh().then(() => {
                this.sitesMapResolver$.then(sitesMapResolver => {
                  sitesMapResolver.refresh();
                  resolve(true);
                });
              });
            });
          });
        });
      });
    });
  }

  /**
   * Allows to wrap the data into a resolver that can be refreshed manually without reloading all the state resolvers.
   * @param resolver the resolver provided to the state (angularjs)
   * @returns the resolver wrapped.
   */
  private wrapResolver(resolver) {
    return resolver().then(firstResponse => {
      firstResponse.refresh = function () {
        return resolver().then(response => {
          if (Array.isArray(firstResponse)) {
            firstResponse.length = 0;
          } else if (response instanceof Map) {
            firstResponse.clear();
            Array.from(response.keys()).forEach(key => {
              firstResponse.set(key, response.get(key));
            });
          } else {
            Object.keys(firstResponse).forEach(key => {
              if (key !== 'refresh') {
                delete firstResponse[key];
              }
            });
          }
          if (response instanceof Map) {
            return firstResponse;
          }
          Object.keys(response).forEach(key => {
            firstResponse[key] = response[key];
          });
          return firstResponse;
        });
      };
      return firstResponse;
    });
  }

  enrichSiteWithLogo = (site: SiteV2) => {
    const { id, site_group_id, name, type, type_name } = site;
    return {
      id,
      site_group_id,
      label: name,
      type,
      typeName: type_name,
      sortKey: `${name.toLowerCase()}${type}`,
      iconClass: `alc-site-icon-inline bg-img-${type_name}`,
    } as any;
  };

  enrichSitesWithLogo(sites: SiteV2[]) {
    return sites.map(this.enrichSiteWithLogo);
  }

  getSitesWithSearchCapability(capabilities) {
    return Object.keys(capabilities || {})
      .filter(siteId => capabilities[siteId]?.includes(CapabilityIdEnum.Answer))
      .map(siteId => this.sitesV2Map.get(+siteId));
  }
}
